import React from 'react';
import { CheckIcon, HeartIcon, ShieldCheckIcon, SparklesIcon, SunIcon, XIcon } from 'lucide-react';
import { CtaButton, CHECKOUT_URL } from '../components/CtaButton';
import { FaqItem } from '../components/FaqItem';
import { bonuses, faqs, features, offerChecklist, painPoints, testimonials } from '../data/content';
const EBOOK_COVER_URL = 'https://cdn.magicpatterns.com/uploads/4GsVtzdd6wrVi37zy8wsZm/Mockup.png';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/1roViPmDXjaiz1sGkRNVUW/Logo_IG.jpg';
const volumeAccents = ['bg-brand-medium', 'bg-brand-rose', 'bg-brand-olive', 'bg-brand-gold', 'bg-brand-sky', 'bg-brand-plum'];
export function SalesPage() {
  return <div className="min-h-screen w-full overflow-x-hidden bg-brand-cream font-body text-slate-800" data-id="element-10" data-mp="1tvwvk52n4bi4">

      {/* Top banner */}
      <header data-id="element-11" data-mp="3qoypuh6ho4uw">
        <div className="bg-brand-medium px-4 py-2 text-center text-xs font-bold tracking-wide text-white sm:text-sm" data-id="element-12" data-mp="3qp8eo1fss5z0">
          <span aria-hidden="true" data-id="element-13" data-mp="3vntg246scvp1">🐾 </span>Digital download — Instant access after purchase
        </div>
        <nav aria-label="Main navigation" className="flex justify-center bg-brand-cream px-5 py-4 sm:py-5" data-id="element-14" data-mp="3al1d8rmmfcxx">
          <a href="#top" className="inline-flex items-center justify-center gap-3 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-medium" data-id="element-15" data-mp="35qx5451mcyv9">
            <img src={LOGO_URL} alt="" className="h-10 w-10 rounded-full bg-black object-contain ring-2 ring-brand-medium/20" data-id="element-16" data-mp="2vw53mq4x1rv7" />
            <span className="font-display text-2xl font-bold tracking-tight text-brand-deep" data-id="element-17" data-mp="3tghxtfnkrahq">
              Prof. Kyra
            </span>
          </a>
        </nav>
      </header>

      <main id="top" data-id="element-18" data-mp="3jitsxww98mlu">

        {/* Hero */}
        <section className="bg-brand-deep px-5 py-12 text-center text-white sm:py-16 lg:py-20" data-id="element-19" data-mp="3fj35o35xsom2">
          <div className="mx-auto max-w-4xl" data-id="element-20" data-mp="2859ey1vrbcs6">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-21" data-mp="28612cpf7a47d">
              CalmPaw™ · By Prof. Kyra
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-7xl" data-id="element-22" data-mp="25ktm69xu3l7i">
              Your Dog Panics Every Time You Leave.{' '}
              <span className="text-brand-gold" data-id="element-23" data-mp="2ahtt0n2icrzo">This System Fixes That.</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/85 sm:text-xl" data-id="element-24" data-mp="1vmdsbz7f9xub">
              The complete <strong className="font-black text-white" data-id="element-25" data-mp="20njpc984879n">6-volume</strong> step-by-step program to end separation anxiety permanently — no guesswork, no punishment, no starting over.
            </p>
            <img src={EBOOK_COVER_URL} alt="The complete CalmPaw™ system — six volumes plus 5 bonuses included" className="mx-auto mt-6 w-full max-w-2xl bg-transparent object-contain" data-id="element-26" data-mp="1rmvaxdet84w8" />
            <p className="mx-auto mt-4 max-w-2xl font-display text-lg font-bold italic leading-relaxed text-white/80 sm:text-xl" data-id="element-27" data-mp="1hviiajl6tap5">
              "Another noise complaint. Another chewed door. Another day you couldn't leave the house without dreading what came next."
            </p>
            <div className="mt-7" data-id="element-28" data-mp="p9apuw6p3sr1">
              <CtaButton label="Download Your Complete System Now" data-id="element-29" data-mp="palrapco4oez" />
            </div>
          </div>
        </section>

        {/* Social proof bar */}
        <section aria-label="Social proof" className="bg-[#ebe4d5] px-5 py-4 text-center text-sm font-bold sm:text-base text-brand-deep" data-id="element-30" data-mp="ya8jqlnf6jvw">
          <span aria-hidden="true" data-id="element-31" data-mp="tcp6np2gkd5j">🐕 </span>Trusted by dog owners who are done dreading the moment they leave
        </section>

        {/* Pain points */}
        <section id="pain-points" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-32" data-mp="137h6kzjijc3h">
          <div className="mx-auto max-w-5xl" data-id="element-33" data-mp="1h06r0zje88yh">
            <div className="mx-auto max-w-2xl text-center" data-id="element-34" data-mp="1gz5ib1punyjx">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-35" data-mp="1czeh042p6015">
                Sound Familiar?
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-36" data-mp="1qw0e3cuqnkui">
                You've Tried Everything. Nothing Sticks.
              </h2>
            </div>
            <div className="mx-auto mt-6 max-w-3xl space-y-4 text-center text-base leading-relaxed text-slate-600" data-id="element-37" data-mp="25zbizpws3mqv">
              <p data-id="element-38" data-mp="2etpwpo1nup7v">
                You bought the toys. You tried "just ignoring it." You searched YouTube at midnight and felt more confused than before. You came home to scratched floors, frantic energy, and a neighbor's text that made your stomach drop.
              </p>
              <p data-id="element-39" data-mp="1n4d2gkoqb7pj">
                <strong className="font-bold text-brand-deep" data-id="element-40" data-mp="1r3vm7d4tdcqs">The problem isn't your dog.</strong> The problem is that most advice treats the symptoms — not the nervous system behind them.
              </p>
            </div>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-5" role="list" data-id="element-41" data-mp="212kvihin9ovf">
              {painPoints.map(point => <li key={point.text} className="flex items-start gap-4 rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-42" data-mp="37iwtahhk14qh">
                  <XIcon className="mt-1 h-5 w-5 flex-none text-brand-rose" aria-hidden="true" data-id="element-43" data-mp="3cfdjric87v6q" />
                  <span className="text-base leading-relaxed text-slate-700" data-id="element-44" data-mp="10t37v7iq3px2">{point.text}</span>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Solution */}
        <section id="solution" className="bg-brand-deep px-5 py-12 text-white sm:py-16" data-id="element-45" data-mp="r2h182szi9qi">
          <div className="mx-auto max-w-3xl text-center" data-id="element-46" data-mp="m474vovix33b">
            <HeartIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-47" data-mp="i33qz7x1dh4q" />
            <p className="mt-5 text-sm font-black uppercase tracking-[0.16em] text-brand-gold" data-id="element-48" data-mp="d4aar7qd7c0v">The Real Problem</p>
            <h2 className="mt-3 font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl" data-id="element-49" data-mp="84nv6rn2djow">
              Separation Anxiety Is Not a Behavior Problem. It&apos;s a Nervous System Problem.
            </h2>
            <div className="mt-5 space-y-4 text-lg leading-relaxed text-white/85" data-id="element-50" data-mp="22i5oywe2bcsd">
              <p data-id="element-51" data-mp="1xjlx6fcsf6s3">
                Your dog isn't being dramatic. They aren't punishing you. Their brain has learned that your departure equals a threat — and no amount of toys, treats, or advice will fix a threat response with surface-level solutions.
              </p>
              <p data-id="element-52" data-mp="3i269u5ijdm48">
                What works is a structured, gradual, science-backed system that retrains the emotional response from the inside out. That is exactly what <strong className="font-bold text-white" data-id="element-53" data-mp="3n20xm070lvl2">CalmPaw™</strong> was built to do.
              </p>
            </div>
            <div className="mt-8 rounded-2xl border border-white/15 bg-white/5 p-6 text-left sm:p-7" data-id="element-54" data-mp="ejcxtx5ep5c6">
              <p className="text-lg leading-relaxed text-white/85" data-id="element-55" data-mp="jig2vhldzbk5">
                <strong className="font-black text-white" data-id="element-56" data-mp="5jzr3c8o2uv5">Introducing CalmPaw™</strong> — The complete 6-volume professional system to end separation anxiety permanently. Built on behavioral science. Designed for real life. Created by Prof. Kyra.
              </p>
            </div>
          </div>
        </section>

        {/* Volumes */}
        <section id="inside" className="bg-[#eef0e6] px-5 py-12 sm:py-16" data-id="element-57" data-mp="2ua90cl2k766a">
          <div className="mx-auto max-w-6xl" data-id="element-58" data-mp="2mr832hicq8rd">
            <div className="mx-auto max-w-2xl text-center" data-id="element-59" data-mp="2huhndvaglf6h">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-60" data-mp="2hw2j3o8if45x">
                The Complete System
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-61" data-mp="2rrw29qgqhvwo">
                6 Volumes. 300+ Pages. One Permanent Solution.
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-62" data-mp="1z407iraq6e2j">
                Each volume solves one layer of the problem. Together, they create a complete system you can run from home in 14 days.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-63" data-mp="290d0to2cypwr">
              {features.map((feature, index) => <article key={feature.title} className="rounded-2xl border border-brand-medium/15 bg-white p-5 shadow-sm" data-id="element-64" data-mp="1l9z59nkke8xf" data-mpi={index}>
                  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-full font-display text-lg font-bold text-white ${volumeAccents[index]}`} data-id="element-65" data-mp="utnuqd6f4cwb">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-66" data-mp="14qaawonsufs0">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-xs font-black uppercase tracking-wide text-brand-medium" data-id="element-67" data-mp="1dt1puyfvvtoe">
                    {feature.pain}
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-slate-600" data-id="element-68" data-mp="1ips1n9kno340">
                    {feature.description}
                  </p>
                </article>)}
            </div>
          </div>
        </section>

        {/* Bonuses */}
        <section id="bonuses" className="bg-[#f1ece0] px-5 py-12 sm:py-16" data-id="element-69" data-mp="1wjjeaqtoo33y">
          <div className="mx-auto max-w-5xl" data-id="element-70" data-mp="21i45k0qyjcg9">
            <div className="mx-auto max-w-2xl text-center" data-id="element-71" data-mp="21iw1jcogvc72">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-72" data-mp="3lzwv74h6d2dq">
                Included Free
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-73" data-mp="3h0ajizgzvu71">
                Plus 5 Premium Bonuses Worth More Than the Course Itself.
              </h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2" data-id="element-74" data-mp="3828ca26o45bb">
              {bonuses.map(bonus => <article key={bonus.title} className="rounded-r-2xl border-l-4 border-brand-medium bg-white px-5 py-5 shadow-sm sm:px-6" data-id="element-75" data-mp="8kqs85zvwlum">
                  <div className="flex items-start gap-4" data-id="element-76" data-mp="3vycn39o10foa">
                    <span className="text-3xl text-brand-gold" aria-hidden="true" data-id="element-77" data-mp="3vwrtbm9jd0rh">{bonus.emoji}</span>
                    <div data-id="element-78" data-mp="3qyq7itkq6ncb">
                      <span className="inline-block rounded-full bg-brand-gold px-3 py-1 text-xs font-black uppercase tracking-wide text-brand-deep" data-id="element-79" data-mp="3qxf44uv6zhlq">
                        {bonus.tag}
                      </span>
                      <h3 className="mt-3 font-display text-xl font-bold leading-snug text-brand-deep" data-id="element-80" data-mp="31dzxpivbcj79">
                        {bonus.title}
                      </h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-81" data-mp="2wh9isyv8vtnf">
                        {bonus.description}
                      </p>
                    </div>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        {/* Author */}
        <section id="author" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-82" data-mp="3kb12p3ovajb7">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-7 text-center md:flex-row md:text-left" data-id="element-83" data-mp="3fdhqsafn6imf">
            <img src={LOGO_URL} alt="Prof. Kyra logo" className="h-40 w-40 flex-none rounded-full border-8 border-white bg-black object-contain shadow-lg" data-id="element-84" data-mp="3u502vx5drt0i" />
            <div data-id="element-85" data-mp="6xftvtpzbjpf">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-86" data-mp="6yzaxmjglys9">
                Meet Your Guide
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-87" data-mp="lkwpr8lnjyn5">
                Prof. Kyra
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-slate-700" data-id="element-88" data-mp="qjhg8gb3r3ye">
                Prof. Kyra is a dog behavior educator and the creator of the CalmPaw™ methodology — a structured, science-backed approach to separation anxiety that focuses on emotional regulation, not obedience.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-slate-700" data-id="element-89" data-mp="2su39gmbxo4z">
                Her work has helped hundreds of dog owners reclaim their independence while building a calmer, more confident companion. CalmPaw™ is her most complete system to date.
              </p>
              <p className="mt-4 text-sm font-bold text-brand-medium" data-id="element-90" data-mp="3u1rhpt0wvymg">
                profkyra@gmail.com · profkyra.com
              </p>
            </div>
          </div>
        </section>

        {/* Social proof */}
        <section id="testimonials" className="bg-[#eef0e6] px-5 py-12 sm:py-16" data-id="element-91" data-mp="3kzibhfcoerev">
          <div className="mx-auto max-w-5xl text-center" data-id="element-92" data-mp="1qkuwojc2sdfs">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-93" data-mp="1lt37od8kifrr">
              What Owners Say
            </p>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-94" data-mp="1gv1n1nvhu8e6">
              Real Results From Real Dog Owners
            </h2>
            <ul className="mt-8 grid gap-4 text-left md:grid-cols-3" role="list" data-id="element-95" data-mp="29j7aiidxmbhp">
              {testimonials.map(testimonial => <li key={testimonial.name} className="rounded-2xl border border-brand-medium/15 bg-white p-6 shadow-sm" data-id="element-96" data-mp="24jcfc2n4izqu">
                  <blockquote className="text-base leading-relaxed text-slate-700" data-id="element-97" data-mp="20gfu04678pj7">
                    "{testimonial.quote}"
                  </blockquote>
                  <p className="mt-4 text-sm font-black text-brand-deep" data-id="element-98" data-mp="p3de7bbxc403">
                    {testimonial.name}
                    <span className="block font-bold text-brand-medium" data-id="element-99" data-mp="k3hjy3t13qxq">{testimonial.role}</span>
                  </p>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Offer */}
        <section id="offer" className="bg-brand-deep px-5 py-12 sm:py-16" data-id="element-100" data-mp="30r8rnvq5ssh4">
          <div className="mx-auto max-w-xl rounded-3xl bg-white p-7 text-center shadow-2xl sm:p-8" data-id="element-101" data-mp="34r97nkswpbqa">
            <SparklesIcon className="mx-auto h-9 w-9 text-brand-medium" aria-hidden="true" data-id="element-102" data-mp="39j11c47czxkn" />
            <p className="mt-4 text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-103" data-mp="3ehubpnrcn8gl">
              Your Complete Bundle
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-deep sm:text-4xl" data-id="element-104" data-mp="3jf43ha27nw7l">
              Everything You Need. One Download.
            </h2>
            <p className="mt-3 text-base leading-relaxed text-slate-600" data-id="element-105" data-mp="3shvbt0zsibv0">
              The complete CalmPaw™ system — all 6 volumes plus all 5 bonuses — available instantly as a professional digital bundle.
            </p>
            <ul className="mt-7 space-y-3 text-left" role="list" data-id="element-106" data-mp="1y43y9qecb7ua">
              {offerChecklist.map(item => <li key={item} className="flex items-start gap-3 text-[15px] leading-snug text-slate-700" data-id="element-107" data-mp="12ruo6vrwr8ki">
                  <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-brand-olive" aria-hidden="true" data-id="element-108" data-mp="yqspcg1wkyj2" />
                  <span data-id="element-109" data-mp="1cqtonr2irxwf">{item}</span>
                </li>)}
            </ul>
            <div className="mt-8 flex flex-col items-center border-t border-brand-medium/15 pt-7 text-center" data-id="element-110" data-mp="17rz3iih2xx95">
              <p className="text-base font-bold text-slate-500 line-through" data-id="element-111" data-mp="k3fyqcx8o2lt">59.99 USD</p>
              <p className="mt-1 font-display text-5xl font-bold leading-none text-brand-deep" data-id="element-112" data-mp="f4mk2h67u5k2">
                23.99 <span className="align-middle text-2xl" data-id="element-113" data-mp="ttiyqx2s2gtn">USD</span>
              </p>
              <p className="mt-2 text-sm font-bold text-slate-600" data-id="element-114" data-mp="p1hgmuixhvvp">
                One-time payment — Yours forever
              </p>
            </div>
            <div className="mt-6 rounded-2xl bg-[#eef0e6] p-5 text-left" data-id="element-115" data-mp="1a3oaoks20aq">
              <p className="text-[15px] leading-relaxed text-slate-700" data-id="element-116" data-mp="3shogsr24xazq">
                <span aria-hidden="true" data-id="element-117" data-mp="2806yjisx3g89">🛡️ </span>
                <strong className="font-black text-brand-deep" data-id="element-118" data-mp="27yces7tdtu5r">7-Day Peace-of-Mind Guarantee.</strong>{' '}
                If you follow the system and don&apos;t see measurable progress, contact us at{' '}
                <a href="mailto:profkyra@gmail.com" className="font-bold text-brand-medium underline underline-offset-2" data-id="element-119" data-mp="1yxxrj2wuy4zm">profkyra@gmail.com</a>{' '}
                and we&apos;ll make it right.
              </p>
            </div>
            <CtaButton label="Download Your Complete System Now" className="mt-6 w-full" data-id="element-120" data-mp="2vqjffj654naz" />
            <p className="mt-4 text-xs font-bold tracking-wide text-slate-500" data-id="element-121" data-mp="2hqie62lyrduz">
              🔒 Secure checkout <span aria-hidden="true" data-id="element-122" data-mp="2mp36lfuz4t8v">|</span> 📩 Instant delivery <span aria-hidden="true" data-id="element-123" data-mp="2mniavmwxb49f">|</span> 🛡️ 7-day guarantee
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-brand-cream px-5 py-12 sm:py-16" data-id="element-124" data-mp="1ba6f7zbbhryc">
          <div className="mx-auto max-w-3xl" data-id="element-125" data-mp="1b83dii0syads">
            <div className="text-center" data-id="element-126" data-mp="3czlpvjh5xiq1">
              <p className="text-sm font-black uppercase tracking-[0.16em] text-brand-medium" data-id="element-127" data-mp="3cykch8cock55">
                Questions
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-brand-deep sm:text-5xl" data-id="element-128" data-mp="339ani1d4nhl9">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="mt-8 space-y-3" data-id="element-129" data-mp="2u8ul6v84eghz">
              {faqs.map(faq => <FaqItem key={faq.question} {...faq} data-id="element-130" data-mp="2ua5nsrptxi7i" />)}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section id="final-cta" className="bg-brand-deep px-5 py-12 text-center text-white sm:py-16" data-id="element-131" data-mp="2kaokggy1vgep">
          <div className="mx-auto max-w-3xl" data-id="element-132" data-mp="2g931cduxa5w2">
            <SunIcon className="mx-auto h-10 w-10 text-brand-gold" aria-hidden="true" data-id="element-133" data-mp="2gaedb0yiujy9" />
            <h2 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl" data-id="element-134" data-mp="iia3bk3byfzc">
              Your Dog Can Learn To Be Alone. Starting Today.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/85" data-id="element-135" data-mp="1wfl3jorc7xbn">
              Stop the trial and error. Stop the guilt. Stop the noise complaints. Download the complete CalmPaw™ system and begin your 14-day reset right now.
            </p>
            <CtaButton label="Download Your Complete System Now" className="mt-6" data-id="element-136" data-mp="26792sbu4yino" />
          </div>
        </section>

      </main>

      <footer className="bg-[#1f2a21] px-5 py-10 text-center text-white/70" data-id="element-137" data-mp="1dgqp6gvscyw0">
        <ShieldCheckIcon className="mx-auto h-6 w-6 text-brand-gold" aria-hidden="true" data-id="element-138" data-mp="1ditppuukeaez" />
        <p className="mt-4 text-sm" data-id="element-139" data-mp="1iho7ctgspsxi">© Prof. Kyra Digital Solutions. All rights reserved. · profkyra@gmail.com · profkyra.com</p>
        <nav aria-label="Footer navigation" className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm" data-id="element-140" data-mp="1rg07bm3jy32m">
          <a href={CHECKOUT_URL} className="underline-offset-4 hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" data-id="element-141" data-mp="2xx3si9lwoacv">
            Get the System
          </a>
        </nav>
        <p className="mx-auto mt-6 max-w-3xl text-xs leading-relaxed text-white/55" data-id="element-142" data-mp="2ejmuf0pfnxez">
          This product is for educational purposes only. Results may vary. Not a substitute for veterinary or professional behavioral advice.
        </p>
      </footer>

    </div>;
}